import React, { useState, useEffect, useCallback } from 'react';
import { User, Role } from '../../types';
import { userApi } from '../../services/userApi';
import PermissionGuard from '../Auth/PermissionGuard';
import UserForm from './UserForm';
import './UserList.css';

interface UserListProps {
  roles: Role[];
}

const UserList: React.FC<UserListProps> = ({ roles }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // TODO: Implement permission checks with usePermissions().getModulePermissions('users')

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userApi.getUsers({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        roleId: selectedRole ? Number(selectedRole) : undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      });

      if (response.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedRole, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      await userApi.deleteUser(user.id);
      fetchUsers();
    } catch (err: any) {
      alert(err.message || 'Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      if (user.is_active) {
        await userApi.deactivateUser(user.id);
      } else {
        await userApi.activateUser(user.id);
      }
      fetchUsers();
    } catch (err: any) {
      alert(err.message || 'Failed to update user status');
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!window.confirm(`Reset password for ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      const response = await userApi.resetUserPassword(user.id);
      if (response.success) {
        alert(`Password reset successfully. Temporary password: ${response.data.tempPassword}`);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to reset password');
    }
  };

  const handleUserFormClose = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };

  const handleUserFormSuccess = () => {
    setShowUserForm(false);
    setEditingUser(null);
    fetchUsers();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value === '' ? '' : Number(e.target.value));
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-list-container">
      <div className="user-list-header">
        <h2>User Management</h2>
        <PermissionGuard module="users" action="create">
          <button className="btn btn-primary" onClick={handleCreateUser}>
            Add New User
          </button>
        </PermissionGuard>
      </div>

      <div className="user-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select value={selectedRole} onChange={handleRoleFilterChange}>
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select value={statusFilter} onChange={handleStatusFilterChange}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="user-table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const firstName = user.first_name || '';
              const lastName = user.last_name || '';
              
              // FIX: Use single span for name to prevent excessive spacing
              return (
                <tr key={user.id}>
                  <td>
                    <div className="user-name">
                      <span>{`${firstName} ${lastName}`}</span>
                      {!!user.is_superadmin && (
                        <span className="superadmin-badge">Super Admin</span>
                      )}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.role_name}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td>
                    <div className="action-buttons">
                      <PermissionGuard module="users" action="update">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditUser(user)}
                          title="Edit User"
                        >
                          Edit
                        </button>
                      </PermissionGuard>

                      <PermissionGuard module="users" action="update">
                        <button
                          className={`btn btn-sm ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleUserStatus(user)}
                          disabled={user.is_superadmin}
                          title={user.is_superadmin ? 'Superadmin cannot be deactivated' : (user.is_active ? 'Deactivate User' : 'Activate User')}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </PermissionGuard>

                      <PermissionGuard module="users" action="update">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleResetPassword(user)}
                          title="Reset Password"
                        >
                          Reset Password
                        </button>
                      </PermissionGuard>

                      <PermissionGuard module="users" action="delete">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteUser(user)}
                          disabled={user.is_superadmin}
                          title="Delete User"
                        >
                          Delete
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {users.length === 0 && !loading && (
          <div className="no-data">No users found</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {showUserForm && (
        <UserForm
          user={editingUser}
          roles={roles}
          onClose={handleUserFormClose}
          onSuccess={handleUserFormSuccess}
        />
      )}
    </div>
  );
};

export default UserList;